import { z } from 'zod';
import { ExtensionConditionsSchema } from './conditions.js';
import { ExtensionActionIdSchema } from './extension.js';
// =============================================================================
// ACTION SCHEMAS
// =============================================================================
export const ActionTypeSchema = z.enum(['add-product', 'add-order-custom-attribute']);
// =============================================================================
// CONFIG SCHEMAS - BASE (for global actions)
// =============================================================================
export const AddShopifyCustomAttributeConfigSchema = z
    .object({
    key: z.string(),
    value: z.string().optional(),
})
    .strict();
export const AddShopifyMetafieldConfigSchema = z
    .object({
    namespace: z.string(),
    key: z.string(),
    valueType: z.literal('string'),
    target: z.literal('order'),
    value: z.string().optional(),
})
    .strict();
export const AddProductConfigSchema = z
    .object({
    variantId: z.union([z.string(), z.number()]),
    quantity: z.number().int().positive(),
    customAttributes: z
        .array(z.object({
        key: z.string(),
        value: z.string(),
    }))
        .optional(),
})
    .strict();
// =============================================================================
// CONFIG SCHEMAS - Actions in fields
// =============================================================================
export const FieldAddShopifyCustomAttributeConfigSchema = AddShopifyCustomAttributeConfigSchema.extend({
    useFieldValue: z.boolean().optional(),
})
    .strict()
    .refine((data) => {
    // If useFieldValue is true, value should not be provided
    if (data.useFieldValue === true && data.value !== undefined) {
        return false;
    }
    // If useFieldValue is false or undefined, value can be provided
    return true;
}, {
    message: 'Cannot provide both value and useFieldValue=true',
});
// AddProductConfig doesn't need field variant as it doesn't use field values
export const FieldAddProductConfigSchema = AddProductConfigSchema;
// =============================================================================
// SMART ACTION SCHEMA WITH CONDITIONAL VALIDATION
// =============================================================================
// Helper function to create action with config
function createOrderCreationAction(type, config) {
    return z
        .object({
        id: ExtensionActionIdSchema,
        trigger: z.literal('on-order-creation'),
        type: z.literal(type),
        config,
    })
        .strict();
}
function createDiscountApplicationAction(type, config) {
    return z
        .object({
        id: ExtensionActionIdSchema,
        trigger: z.literal('on-discount-application'),
        type: z.literal(type),
        config,
        when: ExtensionConditionsSchema('on-discount-application').optional(),
    })
        .strict();
}
function createCheckoutInitiationAction(type, config) {
    return z
        .object({
        id: ExtensionActionIdSchema,
        trigger: z.literal('on-checkout-initiation'),
        type: z.literal(type),
        config,
        when: ExtensionConditionsSchema('on-checkout-initiation').optional(),
    })
        .strict();
}
/**
 * Global extension action schema (no field context, no useFieldValue)
 * Uses discriminated union to enforce rules based on trigger type
 */
export const GlobalActionSchema = z.union([
    createOrderCreationAction('add-order-custom-attribute', AddShopifyCustomAttributeConfigSchema),
    createOrderCreationAction('add-product', AddProductConfigSchema),
    createDiscountApplicationAction('add-order-custom-attribute', AddShopifyCustomAttributeConfigSchema),
    createDiscountApplicationAction('add-product', AddProductConfigSchema),
    createCheckoutInitiationAction('add-order-custom-attribute', AddShopifyCustomAttributeConfigSchema),
    createCheckoutInitiationAction('add-product', AddProductConfigSchema),
]);
/**
 * Field action schema (within field context, supports useFieldValue)
 * Uses discriminated union to enforce rules based on trigger type
 */
export const FieldActionSchema = z.union([
    createOrderCreationAction('add-order-custom-attribute', FieldAddShopifyCustomAttributeConfigSchema),
    createOrderCreationAction('add-product', FieldAddProductConfigSchema),
    createDiscountApplicationAction('add-order-custom-attribute', FieldAddShopifyCustomAttributeConfigSchema),
    createDiscountApplicationAction('add-product', FieldAddProductConfigSchema),
    createCheckoutInitiationAction('add-order-custom-attribute', FieldAddShopifyCustomAttributeConfigSchema),
    createCheckoutInitiationAction('add-product', FieldAddProductConfigSchema),
]);
//# sourceMappingURL=actions.js.map